/**
 * A representation of holidays that may affect business operations.
 *
 * **Note**: This is locale agnostic, meaning that it will only list the keys of
 * possible holidays to be mapped. The actual dates of these holidays may
 * vary by location.
 */
export enum Holiday {
  NEW_YEARS_DAY = 'newYearsDay',
  MARTIN_LUTHER_KING_JR_DAY = 'martinLutherKingJrDay',
  MEMORIAL_DAY = 'memorialDay',
  INDEPENDENCE_DAY = 'independenceDay',
  JUNETEENTH_NATIONAL_INDEPENDENCE_DAY = 'juneteenthNationalIndependenceDay',
  LABOR_DAY = 'laborDay',
  VETERANS_DAY = 'veteransDay',
  THANKSGIVING_DAY = 'thanksgivingDay',
  CHRISTMAS_DAY = 'christmasDay'
}

/**
 * Represents a day of the week, reflecting JavaScript's `Date.getDay()` method.
 *
 * _Note_: We can consider moving this to a Date construct if we ever create one, for now
 * it's here since it's only used in conjunction with holidays.
 */
export enum Weekday {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6
}

/**
 * Represents a specific date for a holiday, defined by month and day.
 */
export interface IHolidayDate {
  /**
   * The {@link Holiday} to which the date corresponds.
   */
  holiday: Holiday;

  /**
   * The month of the holiday date. This is 0-indexed (0 = January, 11 = December), to
   * align with JavaScript Date objects.
   */
  month: number;

  /**
   * The day of the holiday date (1-31).
   */
  day: number;

  /**
   * The weekday of the holiday date (0-6, where 0 = Sunday and 6 = Saturday).
   * This is optional and only needed for holidays that fall on a specific weekday
   * of the month (e.g., the third Monday of January).
   *
   * _Note_: This is 0-indexed to align with JavaScript Date objects. It isn't
   * required since we aren't doing any calculations with it, but it may be useful
   * for reference.
   *
   * @default is undefined, meaning the holiday occurs on the same date every year.
   */
  weekday?: Weekday;

  /**
   * Indicates if the holiday date is dynamic, meaning it may change year over year
   * (e.g., Thanksgiving in the US is the fourth Thursday in November).
   *
   * If true, additional logic will be required to calculate the exact date for
   * a given year.
   *
   * @default is false, meaning the holiday occurs on the same date every year.
   */
  dynamic?: boolean;
}
